import { useState, useEffect } from "react";
import ClientLayout from "../../layouts/ClientLayout";
import API from "../../services/api";
import { Camera, MessageSquare, CalendarDays, RefreshCw, HardHat } from "lucide-react";

const DS = {
    indigo: "#4F46E5", indigoLight: "#EEF2FF",
    bg: "#F8FAFC", surface: "#FFFFFF", surfaceAlt: "#F8FAFC",
    border: "#E2E8F0", borderSolid: "#E8E0F5", dark: "#0F172A",
    gray: "#64748B", faint: "#94A3B8", violetFaint: "#F7F3FD",
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getImageUrl = (filename) => {
    if (!filename) return null;
    if (filename.startsWith("http")) return filename;
    return `https://totalassureplus.com/api/uploads/${filename}`;
};

export default function VisitLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchLogs = async () => {
        try {
            const res = await API.get("/work-schedule-logs/client/my-visits");
            setLogs(res.data.logs || []);
        } catch (err) {
            console.error("Visit logs fetch error:", err);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchLogs().finally(() => setLoading(false));
    }, []);

    return (
        <ClientLayout>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap'); * { font-family: 'Montserrat', sans-serif !important; }`}</style>
            <div style={{ padding: "28px 32px", background: DS.bg, minHeight: "100vh" }}>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 14 }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${DS.indigo},#059669)` }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: DS.faint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                                Client · Visit Logs
                            </span>
                        </div>
                        <h1 style={{ fontWeight: 800, fontSize: 28, color: DS.dark, margin: 0 }}>Visit Logs</h1>
                        <p style={{ color: DS.faint, fontSize: 13, margin: "4px 0 0" }}>Progress updates logged by our team during site visits</p>
                    </div>
                    <button
                        onClick={async () => { setRefreshing(true); await fetchLogs(); setRefreshing(false); }}
                        disabled={refreshing}
                        style={{
                            width: 40, height: 40, borderRadius: 11, border: `1.5px solid ${DS.borderSolid}`,
                            background: DS.surface, color: DS.gray, display: "flex", alignItems: "center",
                            justifyContent: "center", cursor: refreshing ? "not-allowed" : "pointer",
                        }}
                        title="Refresh"
                    >
                        <RefreshCw size={16} style={{ animation: refreshing ? "spin 0.7s linear infinite" : "none" }} />
                    </button>
                </div>

                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

                <div style={{ background: DS.surface, borderRadius: 20, border: `1px solid ${DS.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(79,70,229,0.08)" }}>
                    <div style={{ height: 4, background: `linear-gradient(90deg,${DS.indigo},#059669)` }} />
                    <div style={{ padding: "20px 24px" }}>
                        {loading ? (
                            <div style={{ textAlign: "center", padding: "50px 0", color: DS.faint, fontSize: 13 }}>Loading…</div>
                        ) : logs.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "50px 0", color: DS.faint }}>
                                <HardHat size={36} style={{ marginBottom: 10 }} />
                                <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark }}>No visit logs yet</div>
                                <div style={{ fontSize: 13, marginTop: 4 }}>Progress updates from site visits will appear here.</div>
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                {logs.map((log) => (
                                    <div key={log.id} style={{
                                        border: `1px solid ${DS.borderSolid}`, borderRadius: 14, padding: "16px 18px",
                                        background: DS.surfaceAlt, display: "flex", gap: 16, flexWrap: "wrap",
                                    }}>
                                        {log.progress_image && (
                                            <img
                                                src={getImageUrl(log.progress_image)}
                                                alt="Progress"
                                                style={{ width: 90, height: 90, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: `1px solid ${DS.borderSolid}` }}
                                            />
                                        )}
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: DS.indigo, background: DS.indigoLight, padding: "3px 10px", borderRadius: 12 }}>
                                                    {log.task_id || log.phase_id || "Update"}
                                                </span>
                                                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: DS.gray }}>
                                                    <CalendarDays size={12} /> {formatDate(log.added_dt)}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                                <MessageSquare size={14} color={DS.gray} style={{ marginTop: 2, flexShrink: 0 }} />
                                                <p style={{ margin: 0, fontSize: 13, color: DS.dark, lineHeight: 1.6 }}>{log.comment || "No comment provided."}</p>
                                            </div>
                                            {log.added_by_name && (
                                                <div style={{ fontSize: 11, color: DS.faint, marginTop: 8 }}>Logged by {log.added_by_name}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ClientLayout>
    );
}